/*
 * Подсветка шкафа — как в стеллажах (замечание Сергея № 13): «в стойках или
 * в полках, в каждой полке своя; ближе к фасаду или к задней стенке».
 *
 * В полках: под каждой полкой своя LED-лента в профиле, в каждом отсеке, где
 * полка стоит. Выключить можно у отдельной полки. В стойках: две ленты по
 * боковым стенкам каждого пролёта, на всю высоту над дном (над полкой
 * наружных ящиков). Одно или другое — как в ячейке стеллажа.
 *
 * Старая «LED-полка» (тип "led") при загрузке становится обычной полкой с
 * подсветкой.
 */
import { CARCASS_MM } from "./carcass";
import type { InternalItem, WardrobeModule } from "../types";
import { interiorVerticalRangeMm, usableItemPieces } from "./compartments";
import {
  LED_PROFILE_INSET_MM,
  LIGHT_TEMPERATURES,
  NO_LIGHTING,
  type LightingQuantities,
  normalizeLightPosition,
  normalizeLightTemperature,
  type LightPosition,
  type LightTemperature
} from "./lighting";
import { cabinetSectionWidths } from "./moduleRules";

export type CabinetLightMode = "none" | "shelves" | "uprights";

export const CABINET_LIGHT_MODE_NAMES: Record<CabinetLightMode, string> = {
  none: "Без подсветки",
  shelves: "В полках",
  uprights: "В стойках"
};

export const CABINET_LIGHT_MODE_HINTS: Record<Exclude<CabinetLightMode, "none">, string> = {
  shelves: "Под каждой полкой своя лента; у отдельной полки свет можно выключить",
  uprights: "Ленты по обеим стенкам каждого пролёта, на всю высоту"
};

const PANEL_MM = CARCASS_MM;

export const cabinetSupportsLighting = (module: Pick<WardrobeModule, "type">) =>
  module.type === "single" || module.type === "double" || module.type === "mezzanine";

/** Полка, под которой горит свет. */
export const cabinetShelfLit = (item: Pick<InternalItem, "type" | "light">) =>
  item.type === "shelf" && item.light === true;

export const cabinetLightMode = (module: WardrobeModule): CabinetLightMode => {
  if (!cabinetSupportsLighting(module)) return "none";
  if (module.cabinetUprightLight) return "uprights";
  return module.items.some(cabinetShelfLit) ? "shelves" : "none";
};

export const cabinetLightTemperature = (module: Pick<WardrobeModule, "cabinetLightTemperature">): LightTemperature =>
  normalizeLightTemperature(module.cabinetLightTemperature);

export const cabinetLightPosition = (module: Pick<WardrobeModule, "cabinetLightPosition">): LightPosition =>
  normalizeLightPosition(module.cabinetLightPosition);

/** Сменить подсветку шкафа: «в полках» включает свет под всеми полками, «в стойках» — снимает его с полок. */
export const withCabinetLightMode = (module: WardrobeModule, mode: CabinetLightMode): Partial<WardrobeModule> => ({
  cabinetUprightLight: mode === "uprights" ? true : undefined,
  items: module.items.map((item) => item.type === "shelf"
    ? { ...item, light: mode === "shelves" ? true : undefined }
    : item)
});

export interface CabinetShelfStrip {
  itemId: string;
  /** Ось ленты по X (мм от центра корпуса), длина и высота полки (её центр). */
  center: number;
  lengthMm: number;
  y: number;
}

/** Ленты под полками: по одной в каждом отсеке, где стоит освещённая полка. */
export const cabinetShelfStrips = (module: WardrobeModule): CabinetShelfStrip[] => {
  if (cabinetLightMode(module) !== "shelves") return [];
  return module.items.filter(cabinetShelfLit).flatMap((item) =>
    usableItemPieces(module, item).map((piece) => ({
      itemId: item.id,
      center: piece.center,
      lengthMm: Math.max(0, Math.round(piece.width - LED_PROFILE_INSET_MM * 2)),
      y: item.y
    })));
};

export interface CabinetUprightStrip {
  /** Пролёт слева направо и сторона стенки. */
  section: number;
  side: "left" | "right";
  /** Ось ленты по X (мм от центра корпуса). */
  x: number;
  bottom: number;
  top: number;
  lengthMm: number;
}

/** Ленты в стойках: по обеим стенкам каждого пролёта. */
export const cabinetUprightStrips = (module: WardrobeModule): CabinetUprightStrip[] => {
  if (cabinetLightMode(module) !== "uprights") return [];
  const [bottom, top] = interiorVerticalRangeMm(module);
  const from = bottom + LED_PROFILE_INSET_MM;
  const to = top - LED_PROFILE_INSET_MM;
  if (to - from < 100) return [];
  const sections = cabinetSectionWidths(module.width);
  let cursor = -module.width / 2;
  return sections.flatMap((width, section) => {
    const start = cursor;
    cursor += width;
    const left = start + (section === 0 ? PANEL_MM : PANEL_MM / 2);
    const right = cursor - (section === sections.length - 1 ? PANEL_MM : PANEL_MM / 2);
    return (["left", "right"] as const).map((side) => ({
      section,
      side,
      x: side === "left" ? left + 4 : right - 4,
      bottom: from,
      top: to,
      lengthMm: Math.round(to - from)
    }));
  });
};

/** Общая длина лент шкафа, мм. */
export const cabinetLightLengthMm = (module: WardrobeModule) =>
  cabinetShelfStrips(module).reduce((sum, strip) => sum + strip.lengthMm, 0) +
  cabinetUprightStrips(module).reduce((sum, strip) => sum + strip.lengthMm, 0);

/** Профили с лентой по погонным метрам и блок питания на корпус. */
export const cabinetLightingQuantities = (module: WardrobeModule): LightingQuantities => {
  const length = cabinetLightLengthMm(module);
  if (!length) return NO_LIGHTING;
  return { profileM: length / 1000, corners: 0, drivers: 1 };
};

/** «подсветка в полках у передней кромки, тёплый свет 3000 K». */
export const cabinetLightingDescription = (module: WardrobeModule) => {
  const mode = cabinetLightMode(module);
  if (mode === "none") return "";
  const temperature = cabinetLightTemperature(module);
  const position = cabinetLightPosition(module);
  return `подсветка ${mode === "shelves" ? "в полках" : "в стойках"} ${position === "back" ? "у задней стенки" : "у передней кромки"}, ${LIGHT_TEMPERATURES[temperature].name.toLowerCase()} свет ${LIGHT_TEMPERATURES[temperature].kelvin} K`;
};
